import { createRouter, createWebHistory, useLink } from 'vue-router'
import store from '../store'
import SelectionRouter from '@/views/SelectionRouter.vue'
import Cart from '@/views/Cart.vue'
import Login from '@/views/Login.vue'
import NotFound from '@/views/Errors/NotFound.vue'
import ServerError from '@/views/Errors/ServerError.vue'
import Home from '@/views/Home.vue'
import Notice from '@/views/Notice.vue'
import IssuedGiftCard from '@/views/IssuedGiftCard.vue'
import Receipt from '@/views/Receipt.vue'
import Profile from '@/views/Profile.vue'
import ResellerHub from '@/views/ResellerHub.vue'
import Link from '@/views/Link.vue'
import BottleFactory from '@/views/BottleFactory.vue'
import PrivacyPolicy from '@/views/PrivacyPolicy.vue'
import TermsOfService from '@/views/TermsOfService.vue'
import SMSTermsOfService from '@/views/SMSTermsOfService.vue'
import FullPage from '@/views/FullPage.vue'
import config from '@/config'
import { posthog } from '@/composables/usePosthog'
import { getStorageItem } from '@/utils/safeStorage'
import { isSamePathNavigation } from '@/utils/navigation'
import { upsellLayoutFromQuery } from '@/utils/upsellLayout'

const routes = [
  {
    path: '/:pathMatch(.*)',
    component: NotFound
  },
  {
    path: '/500',
    component: ServerError
  },
  {
    path: '/',
    component: Home,
    name: 'Home'
  },
  {
    path: '/:storeSlug?/s/:storeId',
    component: BottleFactory,
    name: 'BottleFactory'
  },
  {
    path: '/login',
    component: Login,
    name: 'NakedLogin'
  },
  {
    path: '/profile',
    component: Profile,
    name: 'Profile'
  },
  // BOT-1108 - the trailing segment is the product deep link. `(.*)` rather than a plain optional
  // param is deliberate and load-bearing: it makes EVERY path under /b/:bottleid/ resolve here, so
  // a stale or mistyped product link renders the store instead of falling through to the catch-all.
  // That matters because NotFound is not an inert 404 - it looks the last path segment up against
  // the merchant's franchise and can push the customer into a different store, and on a failed
  // lookup it deletes the auth token and reloads the same unmatched URL. The five static siblings
  // below still win, because vue-router ranks a static segment above a dynamic one.
  {
    path: '/resellers',
    component: ResellerHub,
    name: 'ResellerHub'
  },
  // Legacy hub path; keep old links and bookmarks working.
  {
    path: '/reseller',
    redirect: '/resellers'
  },
  {
    path: '/:storeSlug?/b/:bottleid/:productSlug(.*)?',
    component: SelectionRouter,
    name: 'Selection'
  },
  {
    path: '/:storeSlug?/b/:bottleid/cart',
    component: Cart,
    name: 'Cart'
  },
  {
    path: '/:storeSlug?/b/:bottleid/checkout',
    component: () => import('@/views/Checkout.vue'),
    name: 'Checkout'
  },
  {
    path: '/:storeSlug?/b/:bottleid/login',
    component: Login,
    name: 'Login'
  },
  {
    path: '/:storeSlug?/b/:bottleid/notice',
    component: Notice,
    name: 'Notice'
  },
  {
    path: '/:storeSlug?/b/:bottleid/receipt',
    component: Receipt,
    name: 'Receipt'
  },
  {
    path: '/l/:id',
    component: Link,
    name: 'Link'
  },
  {
    path: '/p/:route',
    component: FullPage
  },
  {
    path: '/gc/:code',
    component: IssuedGiftCard,
    name: 'IssuedGiftCard'
  },
  {
    path: '/privacy',
    component: PrivacyPolicy
  },
  {
    path: '/terms-of-service',
    component: TermsOfService
  },
  {
    path: '/sms-terms-of-service',
    component: SMSTermsOfService
  },
  {
    path: '/join-our-list',
    component: () => import('@/views/JoinOurList.vue'),
    name: 'JoinOurList',
    meta: { bareLayout: true }
  }
]

const router = createRouter({
  history: createWebHistory(config.BASE_URL),
  routes
})

let persistedQuery = null
let persistedV2Override = false
let persistedUpsellLayout = null

const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
const persistKeys = [...utmKeys, '_gl']

export function getPersistedUtmParams() {
  if (!persistedQuery) return {}
  const utms = {}
  for (const key of utmKeys) {
    if (persistedQuery[key]) utms[key] = persistedQuery[key]
  }
  return utms
}

export function isV2Override() {
  return persistedV2Override
}

// BOT-1422: `?upsell_layout=grid` (or `=list`) previews the other upsell-modal layout without a
// rebuild, so the two designs can be compared side by side. Presentation only, no capability.
export function upsellLayoutOverride() {
  return persistedUpsellLayout
}

router.beforeEach((to, from) => {
  // On first navigation, capture version_2 override
  if (!from.name && to.query.version_2 === 'true') {
    persistedV2Override = true
  }

  // Same, for the BOT-1422 upsell modal layout
  if (!from.name) {
    const layout = upsellLayoutFromQuery(to.query)
    if (layout) persistedUpsellLayout = layout
  }

  // On first navigation, capture UTM and _gl params to persist across client-side redirects
  if (!from.name && !persistedQuery) {
    const params = {}
    for (const key of persistKeys) {
      if (to.query[key]) params[key] = to.query[key]
    }
    if (Object.keys(params).length) persistedQuery = params
  }

  // Merge persisted params into navigations that are missing them
  if (persistedQuery && !to.query.utm_source && !to.query._gl) {
    return { ...to, query: { ...persistedQuery, ...to.query } }
  }
})

router.beforeEach((to, from) => {
  const samePath = isSamePathNavigation(to, from)

  // Reset so "View our other menus" carousel only shows after the new page has loaded
  if (!samePath) {
    store.commit('SET_MAIN_CONTENT_LOADED', false)
  }

  // Ensure token-derived state (including franchiseId) is hydrated on navigation
  if (!store.state.currentTokenMerchant && getStorageItem('token')) {
    store.commit('SET_CURRENT_TOKENS', getStorageItem('token'))
  }

  if (!samePath && store.state.facebookPixelInitialized) {
    window.fbq('track', 'PageView', {
      URL: to.fullPath
    })
  }

  const slugAwareRouteNames = ['BottleFactory', 'Selection', 'Cart', 'Checkout', 'Login', 'Notice', 'Receipt']
  const selectedStoreSlug = store.state.selectedStoreSlug
  if (selectedStoreSlug && slugAwareRouteNames.includes(to.name) && !to.params.storeSlug) {
    return {
      name: to.name,
      params: { ...to.params, storeSlug: selectedStoreSlug },
      query: to.query,
      hash: to.hash
    }
  }

  return true
})

router.afterEach((to, from) => {
  // Navigation succeeded, so clear the one-shot reload guard set by onError.
  try {
    sessionStorage.removeItem('nav-error-reload')
  } catch (e) {
    // ignore
  }

  if (isSamePathNavigation(to, from)) return

  posthog.capture('$pageview', {
    $current_url: window.location.href,
    url: to.fullPath,
    path: to.path,
    route_name: to.name
  })
})

// Last-resort safety net. An uncaught error in a navigation guard or a failed
// lazy-route chunk would otherwise strand the customer on a blank screen with
// no recovery. The self-healing storage helpers already remove any offending
// persisted value, so a single reload re-runs boot cleanly. A one-shot
// sessionStorage flag prevents a reload loop if the error is genuinely
// unrecoverable. Sentry still captures the error via its router instrumentation.
router.onError((error) => {
  console.error('Router navigation error:', error)

  let alreadyReloaded
  try {
    alreadyReloaded = sessionStorage.getItem('nav-error-reload')
    if (!alreadyReloaded) sessionStorage.setItem('nav-error-reload', '1')
  } catch (e) {
    alreadyReloaded = true // storage unavailable - don't risk a reload loop
  }

  if (!alreadyReloaded) window.location.reload()
})

export default router
